import { z } from 'zod'
import type { Membership } from '@/entities/session/model/session'

export const organizationUserRoleFilterSchema = z.enum([
  'User',
  'Manager',
  'Admin',
  'Support',
  'None',
])

const roleFiltersSchema = z
  .preprocess(
    (value) => (Array.isArray(value) ? value : typeof value === 'string' ? [value] : []),
    z.array(organizationUserRoleFilterSchema)
  )
  .catch([])

export const organizationUserSearchSchema = z.object({
  search: z.string().max(200).optional().catch(undefined),
  roles: roleFiltersSchema,
  interns: z.enum(['all', 'hasInterns', 'noInterns']).catch('all'),
  page: z.coerce.number().int().min(1).catch(1),
  pageSize: z.coerce
    .number()
    .pipe(z.union([z.literal(10), z.literal(25), z.literal(50)]))
    .catch(10),
  sortBy: z
    .enum([
      'lastname',
      'email',
      'phone',
      'user',
      'manager',
      'admin',
      'interncount',
      'unratedcount',
      'lastlogin',
    ])
    .catch('lastname'),
  direction: z.enum(['asc', 'desc']).catch('asc'),
  tab: z.enum(['users', 'invites']).catch('users'),
  invitesPage: z.coerce.number().int().min(1).catch(1),
  invitesPageSize: z.coerce
    .number()
    .pipe(z.union([z.literal(10), z.literal(25), z.literal(50)]))
    .catch(10),
  invitesSortBy: z
    .enum(['email', 'rolegranted', 'state', 'createdutc', 'validtill'])
    .catch('validtill'),
  invitesDirection: z.enum(['asc', 'desc']).catch('desc'),
})

export type OrganizationUserSearch = z.infer<typeof organizationUserSearchSchema>
export type OrganizationUserRoleFilter = z.infer<typeof organizationUserRoleFilterSchema>

const educationalInstitutionRoleFilters: OrganizationUserRoleFilter[] = [
  'User',
  'Manager',
  'Admin',
  'Support',
  'None',
]
const placementProviderRoleFilters: OrganizationUserRoleFilter[] = ['User', 'Admin', 'None']

export const getOrganizationUserRoleFilters = (
  organizationType: Membership['organizationType']
): OrganizationUserRoleFilter[] => {
  if (organizationType === 'educational-institution') {
    return educationalInstitutionRoleFilters
  }
  if (organizationType === 'placement-provider' || organizationType === 'platform') {
    return placementProviderRoleFilters
  }
  return []
}

export const normalizeOrganizationUserSearch = (
  search: OrganizationUserSearch,
  organizationType: Membership['organizationType']
): OrganizationUserSearch => {
  const allowedRoles = new Set(getOrganizationUserRoleFilters(organizationType))
  const roles = search.roles.filter((role) => allowedRoles.has(role))
  const sortBy =
    (organizationType !== 'educational-institution' && search.sortBy === 'manager') ||
    (organizationType === 'platform' &&
      (search.sortBy === 'interncount' || search.sortBy === 'unratedcount'))
      ? 'lastname'
      : search.sortBy

  const interns = organizationType === 'platform' ? 'all' : search.interns
  return interns === search.interns &&
    roles.length === search.roles.length &&
    sortBy === search.sortBy
    ? search
    : { ...search, roles, sortBy, interns, page: 1 }
}

export const defaultOrganizationUserSearch: OrganizationUserSearch = {
  roles: [],
  interns: 'all',
  page: 1,
  pageSize: 10,
  sortBy: 'lastname',
  direction: 'asc',
  tab: 'users',
  invitesPage: 1,
  invitesPageSize: 10,
  invitesSortBy: 'validtill',
  invitesDirection: 'desc',
}
